<script setup>
import { useI18n } from 'vue-i18n';
import { ref, onMounted, watch, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { applySignatureVariables } from 'dashboard/helper/editorHelper';

const props = defineProps({
  htmlSignature: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['update']);
const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');

const htmlContent = ref(props.htmlSignature || '');
const previewFrame = ref(null);

// The stored template keeps the raw {{...}} tokens; the preview resolves them with the current
// user's data so the admin sees a real visual (incl. their actual {{email_signature_picture}} URL).
const resolvedHtml = computed(() =>
  applySignatureVariables(htmlContent.value, currentUser.value || {})
);

const iframeHtml = computed(
  () => `
  <html>
    <head>
      <style>
        body {
          margin: 0;
          padding: 0;
          transform-origin: top left;
          width: fit-content;
          min-width: 100%;
          overflow: hidden;
        }
      </style>
    </head>
    <body>${resolvedHtml.value}</body>
  </html>
`
);

const updatePreview = () => {
  if (previewFrame.value) {
    previewFrame.value.onload = () => {
      // Safari can give the srcdoc document an opaque origin even though the sandbox
      // allows same-origin, which makes reaching into the frame throw a SecurityError.
      // The preview itself still renders, it just stays unscaled, so don't let it bubble
      // up to window.onerror.
      try {
        const doc =
          previewFrame.value.contentDocument ||
          (previewFrame.value.contentWindow &&
            previewFrame.value.contentWindow.document);

        if (!doc) return;

        const body = doc.body;
        const iframe = previewFrame.value;

        if (
          body.offsetWidth > iframe.clientWidth ||
          body.offsetHeight > iframe.clientHeight
        ) {
          const scaleX = iframe.clientWidth / body.offsetWidth;
          const scaleY = iframe.clientHeight / body.offsetHeight;
          const scale = Math.min(scaleX, scaleY, 1);
          body.style.transform = `scale(${scale})`;
        }
      } catch (e) {
        // frame access blocked, the preview renders unscaled
      }
    };
  }
};

const updateSignature = () => {
  emit('update', htmlContent.value);
};

watch(
  () => props.htmlSignature,
  newValue => {
    htmlContent.value = newValue;
    updatePreview();
  }
);

watch(previewFrame, newValue => {
  if (newValue) {
    updatePreview();
  }
});

onMounted(() => {
  updatePreview();
});
</script>

<template>
  <div class="html-signature-editor">
    <label class="block mb-2">
      {{ t('INBOX_MGMT.EDIT.SIGNATURE.HTML_CONTENT') }}
    </label>

    <textarea
      v-model="htmlContent"
      class="code-editor mb-4 w-full h-40 font-mono text-xs p-2 border border-slate-300 rounded dark:border-slate-700 dark:bg-slate-800"
      @input="updateSignature"
    />
    <p
      class="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-line"
      v-html="t('INBOX_MGMT.EDIT.SIGNATURE.HTML_CONTENT_HELP')"
    />

    <div v-if="htmlSignature" class="mb-2">
      <label class="block mb-2">
        {{ t('INBOX_MGMT.EDIT.SIGNATURE.PREVIEW') }}
      </label>
      <div
        class="preview-container border border-slate-300 p-4 rounded dark:border-slate-700"
      >
        <iframe
          ref="previewFrame"
          class="w-full h-40 bg-white"
          sandbox="allow-same-origin"
          :srcdoc="iframeHtml"
        />
      </div>
    </div>
  </div>
</template>
